import { entries, record, scalar, type Answers, type Entry } from "./lib/form-model";

const immigrationFields: Record<string, string> = {
  "pf.identity.other_names": "names",
  "pf.location.country_of_legal_residence": "country_of_legal_residence",
  "pf.location.current_country": "current_country",
  "pf.location.apply_outside_residence": "apply_outside_residence",
  "pf.location.expected_visa_application_country": "expected_visa_application_country",
  "pf.es.consular_district_residence": "consular_district",
  "pf.es.countries_of_residence_5y": "countries_of_residence_5y",
  "pf.us.current_status": "current_status",
  "pf.us.status_expiry": "status_expiry",
  "pf.us.i94_number": "i94_number",
  "pf.us.alien_number": "alien_number",
  "pf.us.ssn": "ssn",
  "pf.es.nie_held": "nie_held",
  "pf.es.nie_number": "nie_number",
  "pf.it.previous_italian_permits": "previous_italian_permits",
  "pf.us.prior_petitions": "prior_petitions",
  "pf.us.visa_refusal": "visa_refusal",
  "pf.us.entry_refusal": "entry_refusal",
  "pf.us.overstay": "overstay",
  "pf.us.status_violation": "status_violation",
  "pf.us.unauthorized_work": "unauthorized_work",
};

const yesNoDetailFields = new Set([
  "previous_italian_permits",
  "prior_petitions",
  "visa_refusal",
  "entry_refusal",
  "overstay",
  "status_violation",
  "unauthorized_work",
]);

type YesNoDetail = { answer?: string | null; details?: string | null };
type FamilyResponse = {
  accompanying?: string | null;
  members?: Array<Record<string, string | null>>;
};
type ImmigrationResponse = {
  other_names?: { names?: Array<Record<string, string | null>> } | null;
  current_residence?: Record<string, unknown> | null;
  immigration_records?: Record<string, unknown> | null;
  applications_and_entry?: Record<string, unknown> | null;
  past_stays?: Record<string, unknown> | null;
};
type LegalResponse = {
  arrested?: YesNoDetail | null;
  charged?: YesNoDetail | null;
  convicted?: YesNoDetail | null;
};

const IMMIGRATION_PAGE_ORDER = [
  "other_names",
  "current_residence",
  "immigration_records",
  "applications_and_entry",
  "past_stays",
] as const;

type ImmigrationPage = (typeof IMMIGRATION_PAGE_ORDER)[number];

const FIELD_PAGE: Record<string, ImmigrationPage> = {
  names: "other_names",
  country_of_legal_residence: "current_residence",
  current_country: "current_residence",
  apply_outside_residence: "current_residence",
  expected_visa_application_country: "current_residence",
  consular_district: "current_residence",
  countries_of_residence_5y: "current_residence",
  current_status: "immigration_records",
  status_expiry: "immigration_records",
  i94_number: "immigration_records",
  alien_number: "immigration_records",
  ssn: "immigration_records",
  nie_held: "immigration_records",
  nie_number: "immigration_records",
  previous_italian_permits: "immigration_records",
  prior_petitions: "applications_and_entry",
  visa_refusal: "applications_and_entry",
  entry_refusal: "applications_and_entry",
  overstay: "past_stays",
  status_violation: "past_stays",
  unauthorized_work: "past_stays",
};

function immigrationPageForFieldId(id: string): ImmigrationPage | null {
  const key = immigrationFields[id];
  return key ? FIELD_PAGE[key] || null : null;
}

function immigrationPage(ids: string[]): ImmigrationPage {
  return immigrationPageForFieldId(ids.find((id) => immigrationPageForFieldId(id) != null) || "") || "immigration_records";
}

function yesNoDetail(value: Answers[string]) {
  const answer = (record(value).value || scalar(value) || "").trim().toLowerCase() || null;
  if (!answer) return { answer: null, details: null };
  if (answer === "no") return { answer: "no" };
  const details = record(value).explanation || null;
  return details ? { answer, details } : { answer };
}

export function accompanyingFamilyPayload(answers: Answers) {
  const accompanying = scalar(answers["pf.family.accompanying"]).trim().toLowerCase();
  const members = accompanying === "yes"
    ? entries(answers["pf.family.members"]).map((member) => ({
      full_name: (member.full_name || [member.given_names, member.surnames].filter(Boolean).join(" ")).trim(),
      birth_date: member.birth_date,
      nationality: member.nationality || member.passport_country,
      relationship: member.relationship,
    })).filter((member) => member.full_name && member.birth_date && member.nationality && member.relationship)
    : [];
  return { accompanying, members };
}

export function immigrationHistoryPayload(ids: string[], answers: Answers) {
  const page = immigrationPage(ids);
  const body: Record<string, unknown> = { page };
  for (const id of ids) {
    const key = immigrationFields[id];
    if (!key || immigrationPageForFieldId(id) !== page) continue;
    const value = answers[id];
    if (key === "names") {
      body.names = entries(value).map((name) => ({
        given_name: name.given_name || null,
        middle_name: name.middle_name || null,
        family_name: name.family_name || null,
      }));
    } else if (key === "countries_of_residence_5y") {
      body[key] = scalar(value).split(",").map((code) => code.trim()).filter(Boolean);
    } else if (yesNoDetailFields.has(key)) {
      const detail = yesNoDetail(value);
      if (detail.answer) body[key] = detail;
    } else if (key === "nie_number") {
      if (scalar(answers["pf.es.nie_held"]).trim().toLowerCase() === "yes") {
        const number = scalar(value).trim();
        if (number) body[key] = number;
      } else {
        body[key] = null;
      }
    } else if (key === "expected_visa_application_country") {
      if (scalar(answers["pf.location.apply_outside_residence"]).trim().toLowerCase() === "yes") {
        const country = scalar(value).trim();
        if (country) body[key] = country;
      }
    } else if (key === "current_country") {
      continue;
    } else {
      const text = scalar(value).trim();
      if (text) body[key] = text;
    }
  }
  return body;
}

/** One POST body per immigration page. The API rejects fields that belong on another page. */
export function immigrationHistoryPayloads(ids: string[], answers: Answers) {
  const grouped = new Map<ImmigrationPage, string[]>();
  for (const id of ids) {
    const page = immigrationPageForFieldId(id);
    if (!page) continue;
    const list = grouped.get(page) || [];
    list.push(id);
    grouped.set(page, list);
  }
  const residenceIds = grouped.get("current_residence");
  if (residenceIds) {
    for (const id of ["pf.location.apply_outside_residence", "pf.location.expected_visa_application_country"]) {
      if (!residenceIds.includes(id)) residenceIds.push(id);
    }
  }
  return IMMIGRATION_PAGE_ORDER.filter((page) => grouped.has(page)).map((page) =>
    immigrationHistoryPayload(grouped.get(page) || [], answers),
  ).filter(immigrationPageHasContent);
}

function immigrationPageHasContent(body: Record<string, unknown>) {
  if (body.page === "current_residence") return Boolean(body.country_of_legal_residence);
  if (body.page === "other_names") return Object.prototype.hasOwnProperty.call(body, "names");
  return Object.entries(body).some(([key, value]) => {
    if (key === "page") return false;
    if (value == null || value === "") return false;
    if (typeof value === "object" && !Array.isArray(value) && !(value as { answer?: string }).answer) return false;
    if (Array.isArray(value) && !value.length) return false;
    return true;
  });
}

export function legalHistoryPayload(answers: Answers) {
  return {
    arrested: yesNoDetail(answers["pf.legal.arrested"]),
    charged: yesNoDetail(answers["pf.legal.charged"]),
    convicted: yesNoDetail(answers["pf.legal.convicted"]),
  };
}

export function answersFromAccompanyingFamily(data: FamilyResponse | null | undefined): Answers {
  const next: Answers = {};
  if (data?.accompanying) next["pf.family.accompanying"] = data.accompanying;
  if (data?.accompanying === "yes" && data?.members?.length) {
    next["pf.family.members"] = data.members.map((member) => ({
      full_name: member.full_name || "",
      birth_date: member.birth_date || "",
      nationality: member.nationality || "",
      relationship: member.relationship || "",
    }));
  }
  return next;
}

function putText(next: Answers, page: Record<string, unknown> | null | undefined, key: string, id: string) {
  const value = page?.[key];
  if (typeof value === "string" && value.trim()) next[id] = value;
}

function putYesNo(next: Answers, page: Record<string, unknown> | null | undefined, key: string, id: string) {
  const value = page?.[key];
  if (!value || typeof value !== "object") return;
  const detail = value as YesNoDetail;
  if (detail.answer || detail.details) next[id] = { value: detail.answer || "", explanation: detail.details || "" };
}

export function answersFromImmigrationHistory(data: ImmigrationResponse | null | undefined): Answers {
  const next: Answers = {};
  const names = data?.other_names?.names;
  if (Array.isArray(names)) {
    if (names.length) {
      next["pf.identity.other_names"] = names.map((name) => ({
        given_name: name.given_name || "",
        middle_name: name.middle_name || "",
        family_name: name.family_name || "",
      })) as Entry[];
      next["pf.identity.other_names_used"] = "yes";
    } else {
      next["pf.identity.other_names_used"] = "no";
    }
  }
  const residence = data?.current_residence;
  putText(next, residence, "country_of_legal_residence", "pf.location.country_of_legal_residence");
  putText(next, residence, "current_country", "pf.location.current_country");
  putText(next, residence, "apply_outside_residence", "pf.location.apply_outside_residence");
  putText(next, residence, "expected_visa_application_country", "pf.location.expected_visa_application_country");
  putText(next, residence, "consular_district", "pf.es.consular_district_residence");
  const countries = residence?.countries_of_residence_5y;
  if (Array.isArray(countries) && countries.length) next["pf.es.countries_of_residence_5y"] = countries.join(",");

  const records = data?.immigration_records;
  putText(next, records, "current_status", "pf.us.current_status");
  putText(next, records, "status_expiry", "pf.us.status_expiry");
  putText(next, records, "i94_number", "pf.us.i94_number");
  putText(next, records, "alien_number", "pf.us.alien_number");
  putText(next, records, "ssn", "pf.us.ssn");
  putText(next, records, "nie_held", "pf.es.nie_held");
  putText(next, records, "nie_number", "pf.es.nie_number");
  putYesNo(next, records, "previous_italian_permits", "pf.it.previous_italian_permits");

  const applications = data?.applications_and_entry;
  putYesNo(next, applications, "prior_petitions", "pf.us.prior_petitions");
  putYesNo(next, applications, "visa_refusal", "pf.us.visa_refusal");
  putYesNo(next, applications, "entry_refusal", "pf.us.entry_refusal");

  const stays = data?.past_stays;
  putYesNo(next, stays, "overstay", "pf.us.overstay");
  putYesNo(next, stays, "status_violation", "pf.us.status_violation");
  putYesNo(next, stays, "unauthorized_work", "pf.us.unauthorized_work");
  return next;
}

export function answersFromLegalHistory(data: LegalResponse | null | undefined): Answers {
  const next: Answers = {};
  const page = data as Record<string, unknown> | null | undefined;
  putYesNo(next, page, "arrested", "pf.legal.arrested");
  putYesNo(next, page, "charged", "pf.legal.charged");
  putYesNo(next, page, "convicted", "pf.legal.convicted");
  return next;
}
